// lib/intake-link-model.js — the shared vocabulary of an intake link's
// lifecycle: what each status is CALLED, what each worker flag MEANS, and what
// a person may DO with a link in that status.
//
// Two staff surfaces read from here — the SPA's Intake Review panel
// (components/intake-links-panel.jsx) and Zephyr, the standalone front-office
// site (src/zephyr/*) — so a status or flag gains its wording once, not twice.
// Pure data + pure functions: no React, no DOM, no imports. Presentation
// (badge classes, button styling) belongs to each surface, not here.
//
// The sets below are answerable against the server:
//   STATUSES  ↔ the CHECK constraint on intake_links.status (migration 040)
//   FLAG_LABEL ↔ every literal intakeLink/worker.js and links.js write to flags[]
// tests/unit/v3/intake-link-model.test.js derives both from that source and
// fails when the server grows a state this file has no words for.

// Status flow: sent → opened → submitted → processing → completed | flagged.
// expired/voided are terminal administrative states.
export const STATUSES = [
  'sent', 'opened', 'submitted', 'processing', 'completed', 'flagged', 'expired', 'voided',
];

// 'processing' is a worker-internal transient — staff see it as "Submitted",
// because from their side nothing has changed since the patient hit send.
export const STATUS_LABEL = {
  sent: 'Sent',
  opened: 'Opened',
  submitted: 'Submitted',
  processing: 'Submitted',
  completed: 'Chart created',
  flagged: 'Needs attention',
  expired: 'Expired',
  voided: 'Voided',
};

// Each label finishes the sentence "this submission …", and where a person must
// act it names the act. Never a bare error code: the reader is a front-desk
// clerk deciding whether to open Elation, re-send, or leave it alone.
export const FLAG_LABEL = {
  carrier_unmapped: 'Payer not matched — set payer in Elation',
  carrier_low_confidence: 'Payer linked by best match (see chart note) — verify in Elation',
  carrier_autocorrected: 'Payer auto-corrected — eligibility confirmed an alternate (see chart note)',
  carrier_not_enrolled: 'Payer not enrolled for eligibility',
  pdf_card_skipped: 'PDF card not attached',
  low_confidence_ocr: 'Low-confidence card scan',
  image_unreadable: 'Card image unreadable',
  eligibility_inactive: 'Coverage inactive',
  eligibility_error: 'Eligibility check errored',
  eligibility_id_mismatch: 'Eligibility ID mismatch',
  insurance_id_missing: 'Policy id missing',
  elation_write_failed: 'Elation write failed',
  patient_duplicate: 'Matched existing Elation chart — verify it\'s the same person',
  min_data_gate: 'Incomplete submission',
  worker_interrupted: 'Processing interrupted — resend a fresh link',
  expired_unprocessed: 'Expired before processing — resend a fresh link',
  appointment_failed: 'Appointment not booked in Elation — retry or book manually',
};

// Unknown values fall through to the raw token rather than disappearing: a
// state we have no wording for must still be visible to whoever is looking.
export function statusLabel(status) {
  return STATUS_LABEL[status] || String(status || '');
}

export function flagLabel(flag) {
  return FLAG_LABEL[flag] || String(flag || '');
}

// What a person may do with a link in each status. This mirrors what the
// SERVER will accept, so a button never promises an action the API refuses:
//   resend  → links.resendLink accepts sent/opened/expired
//   void    → links.voidLink accepts sent/opened only (an expired link is
//             already inert; voiding one would purge nothing and change nothing)
//   retry   → links.retryFlagged accepts flagged, pre-Elation, draft surviving
//             (the route still 404s with its own explanation when it can't)
//   details → anything the patient has actually submitted, plus flagged
//   openChart → a chart exists (see actionsForLink for the data guard)
// A voided link is finished: nothing staged, nothing to open, no way back.
const ACTIONS_BY_STATUS = {
  sent: ['resend', 'void'],
  opened: ['resend', 'void'],
  submitted: ['details'],
  processing: ['details'],
  completed: ['details', 'openChart'],
  flagged: ['retry', 'details'],
  expired: ['resend'],
  voided: [],
};

export function actionsForStatus(status) {
  return ACTIONS_BY_STATUS[status] || [];
}

// Status availability, narrowed AND widened by what the row actually carries.
//
// Narrowed: older rows (and any completed link whose chart id never made it
// back) have no chart to open, so that link is dropped rather than rendered
// pointing at /patient//.
//
// Widened: a link with card photos behind it can always be opened, whatever its
// status. Photos arrive while the patient is still filling the form — the link
// is 'opened', not submitted — and that is exactly when the front desk wants to
// look at them. Without this the images would be unreachable from the queue
// until the worker had run.
//
// Widened again by an appointment (self-scheduling PR5): the Appointment line
// and its buttons live in the detail, so a link that carries one can always be
// opened — a held or booked time on an `opened` link included.
//
// Narrowed: the link-level retry (`POST /:id/retry` → `links.retryFlagged`)
// refuses once a chart exists (`elation_patient_id IS NULL`); a flagged link
// with a chart would otherwise offer a button that always 404s beside the
// detail's working Retry booking (PR5 adjudication F-H).
export function actionsForLink(link) {
  let actions = actionsForStatus(link && link.status);
  if (link && !link.elationPatientId) actions = actions.filter((a) => a !== 'openChart');
  if (link && link.elationPatientId) actions = actions.filter((a) => a !== 'retry');
  if (link && (Number(link.documentCount) > 0 || link.appointment) && !actions.includes('details')) {
    actions = [...actions, 'details'];
  }
  return actions;
}

// ── The self-scheduled appointment (self-scheduling PR5, plan §6d) ───────────
// The server projects every intake_appointments status onto eight VIEW statuses
// (server/services/scheduling/store.js toViewStatus): held → held,
// pending_chart|posting → pending, booked → booked, cancelling → cancelling,
// rescheduling → rescheduling (in flight — visible to every clerk, buttons
// withheld), failed|indeterminate → failed, cancelled → cancelled,
// expired → expired (released rows are omitted).
// tests/unit/v3/zephyr-appointments.test.js derives this list from the server
// and fails when the two drift.
export const APPOINTMENT_VIEW_STATUSES = ['held', 'pending', 'booked', 'cancelling', 'rescheduling', 'failed', 'cancelled', 'expired'];

export const APPOINTMENT_STATUS_LABEL = {
  held: 'Time held',
  pending: 'Booking…',
  booked: 'Booked',
  cancelling: 'Cancelling…',
  rescheduling: 'Moving…',
  failed: 'Booking failed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

// Who cancelled: the last history entry that reached `cancelled` says. 'elation'
// when the cancellation happened inside Elation and this side followed it
// (busy.js's reconcile, the recovery sweep, the marker reconcile — the row then
// has no cancelled_by); 'staff' when a person here did it; null when nothing
// says. A staff cancel the recovery sweep settled still reads as 'staff'.
export function appointmentCancelledBy(appt) {
  if (!appt || appt.status !== 'cancelled') return null;
  const hist = Array.isArray(appt.history) ? appt.history : [];
  for (let i = hist.length - 1; i >= 0; i--) {
    const h = hist[i];
    if (h && h.to === 'cancelled') return h.by === 'elation' ? 'elation' : 'staff';
  }
  return appt.cancelledBy ? 'staff' : null;
}

// "Cancelled in Elation" is the one label that depends on more than the status:
// a cancelled row with no cancelled_by is a cancellation nobody here made.
export function appointmentStatusLabel(appt) {
  if (!appt) return '';
  if (appt.status === 'cancelled' && appointmentCancelledBy(appt) === 'elation') return 'Cancelled in Elation';
  return APPOINTMENT_STATUS_LABEL[appt.status] || String(appt.status || '');
}

// What a person may do with the appointment, mirroring what the SERVER accepts:
//   reschedule → POST /:id/appointment/reschedule (booked; pending_chart/failed swap locally) — time-only
//   cancel     → POST /:id/appointment/cancel (booked; pending_chart/failed/indeterminate locally / by reconcile)
//   retry      → POST /:id/appointment/retry (failed | indeterminate, once a chart exists)
// A `pending` row may be mid-post for a few seconds; the server then answers 409
// with a message, so offering the buttons costs a clerk nothing but a retry.
// A failed booking whose time has PASSED cannot be retried (the slot is gone)
// but must stay cancellable — that is how a dead slot is freed.
const APPOINTMENT_ACTIONS = {
  held: [],
  pending: ['reschedule', 'cancel'],
  booked: ['reschedule', 'cancel'],
  // in flight (a staff PATCH Elation has not answered): the route would answer
  // 409 (IN_FLIGHT_MESSAGE) — the buttons are withheld instead, for every clerk,
  // and return when the route or the sweep settles the row (Round 5)
  cancelling: [],
  rescheduling: [],
  failed: ['retry', 'cancel'],
  cancelled: [],
  expired: [],
};

export function appointmentIsPast(appt, now = Date.now()) {
  const ms = appt && appt.start ? Date.parse(appt.start) : NaN;
  return Number.isFinite(ms) && ms <= now;
}

export function appointmentActions(appt, now = Date.now()) {
  if (!appt) return [];
  const actions = APPOINTMENT_ACTIONS[appt.status] || [];
  return appointmentIsPast(appt, now) ? actions.filter((a) => a !== 'retry') : actions;
}

export function chartUrl(elationPatientId) {
  if (!elationPatientId) return null;
  return `https://app.elationemr.com/patient/${encodeURIComponent(elationPatientId)}/`;
}
